import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from './data.service';
import { PatientCashDeskSeachItem } from '../model/patient/patient-pending-cash-desk-search-item';
import { CashDeskUserOpen } from '../model/cash-desk-user-open';
import { AuthService } from './auth.service';
import { CashDeskOpenSearchItem } from '../model/cash-desk-open-search-item';
import { CashDeskOpenEditInfo } from '../model/cash-desk-open-edit-info';
import { CashDesk } from '../model/cash-desk';
import { IFilterComplete } from '../filter-complete/filter-complete';
import { CashDeskManualSearchItem } from '../model/cash-desk-manual-search-item';
import { InvoiceCashDeskAssociationInfo } from '../model/invoice-cash-desk-association-info';
import { ReportViewer } from '../model/report-viewer';
import { CashDeskPaymentFilter } from '../model/cash-desk-payment-filter';
import { VirtualWalletFilter } from '../model/virtual-wallet-filter';

@Injectable()
export class CashDeskService {

  constructor(private http: HttpClient, private dataService: DataService, private authService: AuthService) { }

  private readonly getCashDeskManualSearchItemForAssociateInvoicesEndPoint: string = '/cashDesk/getCashDeskManualSearchItemForAssociateInvoices/';
  private readonly getCashDeskManualSearchItemEndPoint: string = '/cashDesk/getCashDeskManualSearchItem/';
  private readonly getPatientPendingCashDeskSearchItemEndPoint: string = '/cashDesk/getPatientPendingCashDeskSearchItem/';
  private readonly getPatientMadeCashDeskSearchItemEndPoint: string = '/cashDesk/getPatientMadeCashDeskSearchItem/';
  private readonly getCashDeskUserOpenEndPoint: string = '/cashDesk/getCashDeskUserOpen/';
  private readonly getCashDeskUserOpenForManualEndPoint: string = '/cashDesk/getCashDeskUserOpenForManual/';
  private readonly getCashDeskOpenSearchItemsEndPoint: string = '/cashDesk/getCashDeskOpenSearchItems/';
  private readonly getCashDeskOpenEditEndPoint: string = '/cashDesk/getCashDeskOpenEdit/';
  private readonly cashDeskOpenEndPoint: string = '/cashDesk/cashDeskOpen/';
  private readonly cashDeskOpenCloseEndPoint: string = '/cashDesk/cashDeskOpenClose/';
  private readonly getInvoiceCashDeskAssociationInfoEndPoint: string = '/cashDesk/getInvoiceCashDeskAssociationInfo/';
  private readonly invoiceCashDeskAssociateEndPoint: string = '/cashDesk/invoiceCashDeskAssociate/';
  private readonly getCashDeskOpenCloseReportPdfEndPoint: string = '/cashDesk/getCashDeskOpenCloseReportPdf/';
  private readonly getCashDeskPaymentFiltersEndPoint: string = '/cashDesk/getCashDeskPaymentFilters/';
  private readonly getLastCashDeskEndPoint: string = '/cashDesk/getLastCashDesk/';
  private readonly getVirtualWalletFiltersEndPoint: string = '/cashDesk/getVirtualWalletFilters/';

  async getLastCashDesk( medicalAppointmentId: number ): Promise<number | undefined> {
    const params = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService.get<number | undefined>(this.getLastCashDeskEndPoint, params).toPromise();
  }

  async getVirtualWalletFilters(): Promise<VirtualWalletFilter[]> {
    const params = { };
    return await this.dataService.get<VirtualWalletFilter[]>(this.getVirtualWalletFiltersEndPoint, params).toPromise();
  }

  async getCashDeskPaymentFilters(): Promise<CashDeskPaymentFilter[]> {
    const params = {  };
    return await this.dataService.get<CashDeskPaymentFilter[]>(this.getCashDeskPaymentFiltersEndPoint, params).toPromise();
  }

  async invoiceCashDeskAssociate(cashDeskManualId: number, medicalAppointmentId: number): Promise<boolean> {
    const params = { MedicalAppointmentId: medicalAppointmentId, CashDeskManualId: cashDeskManualId };
    return await this.dataService.post<boolean>(this.invoiceCashDeskAssociateEndPoint, params).toPromise();
  }

  async getInvoiceCashDeskAssociationInfo(medicalAppointmentId: number): Promise<InvoiceCashDeskAssociationInfo> {
    const params = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService.get<InvoiceCashDeskAssociationInfo>(this.getInvoiceCashDeskAssociationInfoEndPoint, params).toPromise();
  }

  async getCashDeskManualSearchItemForAssociateInvoices(
    companyIds: number[],
    businessName: string,
    excludeAssociated: boolean,
    number?: number): Promise<CashDeskManualSearchItem[]> {

    const params = {
      companyIds: JSON.stringify(companyIds)
      , businessName: businessName
      , excludeAssociated
      , number: number
    };

    return await this.dataService.get<CashDeskManualSearchItem[]>(this.getCashDeskManualSearchItemForAssociateInvoicesEndPoint, params).toPromise();
  }

  async getCashDeskManualSearchItem(companyIds: number[], businessName: string, number?: number, createDate?: Date): Promise<CashDeskManualSearchItem[]> {

    let date: string;
    if (createDate) {
      date = new Date(createDate.getFullYear(), createDate.getMonth(), createDate.getDate()).toDateString();
    }

    const params = {
      companyIds: JSON.stringify(companyIds)
      , businessName: businessName
      , number: number
      , date: date
    };

    return await this.dataService.get<CashDeskManualSearchItem[]>(this.getCashDeskManualSearchItemEndPoint, params).toPromise();
  }

  async getCashDeskUserOpenBySectorService(sectorId: number, allInvoicePoint: boolean, allService: boolean
    , serviceId?: number, invoicePointId?: number): Promise<CashDeskUserOpen> {
    const params = { sectorId: sectorId, allInvoicePoint: allInvoicePoint, allService: allService, serviceId: serviceId, invoicePointId: invoicePointId };
    return await this.dataService.get<CashDeskUserOpen>(this.getCashDeskUserOpenForManualEndPoint, params).toPromise();
  }

  async cashDeskOpen(user: IFilterComplete, cashDesk: CashDesk): Promise<boolean> {
    const params = { cashDesk: cashDesk, user: user };
    return await this.dataService.post<boolean>(this.cashDeskOpenEndPoint, params).toPromise();
  }

  async cashDeskOpenClose(cashDeskOpenSearchItem: CashDeskOpenSearchItem): Promise<boolean> {
    return await this.dataService.post<boolean>(this.cashDeskOpenCloseEndPoint, cashDeskOpenSearchItem).toPromise();
  }

  async getCashDeskOpenEdit(cashDeskId: number): Promise<CashDeskOpenEditInfo> {
    const params = { cashDeskId: cashDeskId };
    return await this.dataService.get<CashDeskOpenEditInfo>(this.getCashDeskOpenEditEndPoint, params).toPromise();
  }

  async getCashDeskOpenSearchItems(name: string): Promise<CashDeskOpenSearchItem> {
    const params = { name: name };
    return await this.dataService.get<CashDeskOpenSearchItem>(this.getCashDeskOpenSearchItemsEndPoint, params).toPromise();
  }

  async getPatientPendingCashDeskSearchItem(protocol: string, companyIds: number[], patientId?: number
    , medicalAppointmentDate?: Date): Promise<PatientCashDeskSeachItem[]> {

    let date: string;
    if (medicalAppointmentDate) {
      date = new Date(medicalAppointmentDate.getFullYear(), medicalAppointmentDate.getMonth(), medicalAppointmentDate.getDate()).toDateString();
    }

    const params = {
      protocol: protocol
      , companyIds: JSON.stringify(companyIds)
      , patientId: patientId
      , medicalAppointmentDate: date
    };

    return await this.dataService.get<PatientCashDeskSeachItem[]>(this.getPatientPendingCashDeskSearchItemEndPoint, params).toPromise();
  }

  async getPatientMadeCashDeskSearchItem(protocol: string, companyIds: number[], patientId?: number
    , number?: number, medicalAppointmentDate?: Date): Promise<PatientCashDeskSeachItem[]> {

    let date: string;
    if (medicalAppointmentDate) {
      date = new Date(medicalAppointmentDate.getFullYear(), medicalAppointmentDate.getMonth(), medicalAppointmentDate.getDate()).toDateString();
    }

    const params = {
      protocol: protocol
      , companyIds: JSON.stringify(companyIds)
      , patientId: patientId
      , number: number
      , medicalAppointmentDate: date
    };

    return await this.dataService.get<PatientCashDeskSeachItem[]>(this.getPatientMadeCashDeskSearchItemEndPoint, params).toPromise();
  }

  async getCashDeskUserOpenBySectorAndEquipment(sectorId: number, equipmentId: number,
     allInvoicePoint: boolean): Promise<CashDeskUserOpen> {
    const params = { sectorId: sectorId, equipmentId: equipmentId, allInvoicePoint: allInvoicePoint };
    return await this.dataService.get<CashDeskUserOpen>(this.getCashDeskUserOpenEndPoint, params).toPromise();
  }

  async getCashDeskOpenCloseReportPdf(cashDeskOpenId: number): Promise<ReportViewer> {
    const params = { cashDeskOpenId: cashDeskOpenId };
    return await this.dataService.get<ReportViewer>(this.getCashDeskOpenCloseReportPdfEndPoint, params).toPromise();
  }
}
